/* Live nav page — fills the live surface (sits above the fixed BgLayer so the
 * non-glass keycaps + accent banner don't paint behind it). Centers the glass
 * card so the content reads against any theme background. */
.page {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 100%;
  box-sizing: border-box;
  padding: clamp(20px, 4vw, 56px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(16px, 2.4vw, 32px);
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
}

/* Frosted-glass box behind the live content so grades/names/keys stay legible
 * over the theme background (matches the Weather/Feelings/Pledges cards). */
.card {
  box-sizing: border-box;
  width: min(92vw, 1100px);
  max-height: 100%;
  background: rgba(255, 250, 240, 0.86);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1.5px solid var(--token-accent);
  border-radius: var(--token-radius-card);
  box-shadow:
    var(--token-shadow-card),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  padding: clamp(24px, 3vw, 52px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(16px, 2.4vw, 32px);
  overflow-y: auto;
}

/* Settings-tab wrapper (Rewards settings live inside the classroom detail). */
.settings {
  display: flex;
  flex-direction: column;
  gap: clamp(14px, 1.8vw, 24px);
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.title {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(32px, 4vw, 56px);
  line-height: 1.05;
}

.subtitle {
  margin: 4px 0 0;
  opacity: 0.75;
  font-size: clamp(14px, 1.4vw, 20px);
}

/* ---- glass control base ---- */
.editToggle,
.gradeTab,
.gradeTabActive,
.addBtn,
.backBtn,
.deleteBtn,
.undoBtn,
.resetBtn,
.historyBtn {
  min-height: var(--token-touch-min);
  padding: 0 clamp(14px, 1.6vw, 22px);
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.82);
  color: var(--token-text-primary);
  font-family: inherit;
  font-weight: 600;
  font-size: clamp(15px, 1.4vw, 19px);
  cursor: pointer;
  backdrop-filter: blur(16px) saturate(150%);
  -webkit-backdrop-filter: blur(16px) saturate(150%);
}

.editToggleOn,
.gradeTabActive {
  background: var(--token-accent);
  color: rgba(255, 255, 255, 0.98);
  border-color: rgba(255, 255, 255, 0.35);
}

.grades {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(8px, 1vw, 14px);
  align-items: center;
}

.loading,
.hint {
  opacity: 0.7;
}

.errorBanner {
  padding: 12px 18px;
  border-radius: var(--token-radius-card);
  background: rgba(200, 60, 40, 0.16);
  border: 1px solid rgba(200, 60, 40, 0.5);
  color: rgba(120, 20, 12, 0.98);
  font-weight: 600;
  font-size: clamp(15px, 1.4vw, 19px);
}

.notice {
  padding: clamp(20px, 3vw, 40px);
  border-radius: var(--token-radius-card);
  background: var(--token-card-bg);
  box-shadow: var(--token-shadow-card);
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
  max-width: 640px;
}

/* ---- manage-mode add panel — its own space above the grades + teachers ---- */
.managePanel {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(16px, 2.4vw, 36px);
  padding: clamp(14px, 1.8vw, 24px) clamp(16px, 2vw, 28px);
  border-radius: var(--token-radius-card);
  background: var(--token-card-bg);
  box-shadow: var(--token-shadow-card);
}

.manageGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ---- bulk import from a spreadsheet ---- */
.importPanel {
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.4vw, 16px);
  padding: clamp(14px, 1.8vw, 24px) clamp(16px, 2vw, 28px);
  border-radius: var(--token-radius-card);
  background: var(--token-card-bg);
  box-shadow: var(--token-shadow-card);
}

.importRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(10px, 1.4vw, 18px);
}

/* Native file input is driven by the styled button — keep it out of the flow. */
.fileInput {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.importError,
.importSuccess {
  margin: 0;
  font-weight: 600;
  font-size: clamp(14px, 1.3vw, 18px);
}

.importError {
  color: rgba(150, 30, 20, 0.95);
}

.importSuccess {
  color: color-mix(in srgb, var(--token-accent) 80%, #1a1a1a);
}

.importPreview {
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.4vw, 16px);
  padding-top: clamp(6px, 1vw, 12px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.importPreviewHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
  font-size: clamp(14px, 1.3vw, 18px);
}

.importGradeList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: clamp(14px, 1.3vw, 17px);
  opacity: 0.9;
}

.importSkipped {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.importSkippedItem {
  font-size: clamp(13px, 1.2vw, 16px);
  color: rgba(150, 30, 20, 0.9);
}

.manageLabel {
  font-weight: 700;
  font-size: clamp(14px, 1.3vw, 18px);
  opacity: 0.85;
}

/* The selected grade's teacher area, below the grades row. */
.expand {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(12px, 1.6vw, 20px);
  padding-top: clamp(4px, 0.8vw, 12px);
}

.gradeManageRow {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

/* Manual jar-goal editor (Settings → Rewards). */
.goalRow {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.goalInput {
  width: 5em;
  min-height: var(--token-touch-min);
  padding: 0 12px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.9);
  color: var(--token-text-primary);
  font-family: inherit;
  font-size: clamp(16px, 1.6vw, 20px);
  text-align: center;
}

.goalUnit {
  opacity: 0.75;
  font-size: clamp(14px, 1.3vw, 18px);
}

.goalDefaultBtn {
  min-height: var(--token-touch-min);
  padding: 0 14px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  color: var(--token-text-primary);
  font-family: inherit;
  font-size: clamp(13px, 1.2vw, 16px);
  cursor: pointer;
  opacity: 0.8;
}

/* Manage-mode teacher list: one editable row per teacher. */
.manageTeacherList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 760px;
}

/* ---- class name list (names only — no progress shown) ---- */
.classList {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 900px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(12px, 1.6vw, 20px);
}

.classItem {
  display: flex;
  align-items: center;
  gap: 8px;
}

.classButton {
  flex: 1;
  min-height: clamp(64px, 8vw, 96px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 clamp(18px, 2vw, 28px);
  border-radius: var(--token-radius-card);
  border: 1.5px solid rgba(255, 255, 255, 0.5);
  background: var(--token-card-bg);
  box-shadow: var(--token-shadow-card);
  color: var(--token-text-primary);
  font-family: var(--token-font-display);
  font-size: clamp(20px, 2.2vw, 30px);
  cursor: pointer;
  text-align: left;
}

.classButton:hover {
  border-color: var(--token-accent);
}

.earnedPill {
  color: var(--token-accent);
  font-size: 1.4em;
}

.deleteBtn {
  min-height: 40px;
  padding: 0 12px;
  background: rgba(200, 60, 40, 0.14);
  border-color: rgba(200, 60, 40, 0.4);
  color: rgba(150, 30, 20, 0.95);
}

/* Inline two-tap delete confirm (replaces native window.confirm). */
.confirmRow {
  display: flex;
  gap: 6px;
  align-items: center;
}

.confirmDeleteBtn,
.cancelBtn {
  min-height: 40px;
  padding: 0 14px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.5);
  font-family: inherit;
  font-weight: 600;
  font-size: clamp(14px, 1.3vw, 17px);
  cursor: pointer;
  white-space: nowrap;
}

.confirmDeleteBtn {
  background: rgba(200, 60, 40, 0.9);
  border-color: rgba(200, 60, 40, 0.6);
  color: rgba(255, 255, 255, 0.98);
  font-weight: 700;
}

.cancelBtn {
  background: rgba(255, 255, 255, 0.85);
  color: var(--token-text-primary);
}

/* ---- single-class GAMES tracker ---- */
/* Tracker view tightens the card frame: the name sits just under the top
 * edge, and losing the old vertical padding keeps the keys + Undo/Reset
 * on screen once GAMES is fully spelled out. */
.cardTracker {
  padding: 8px clamp(12px, 1.4vw, 20px) clamp(14px, 1.8vw, 24px);
}

.tracker {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(12px, 2vw, 24px);
  padding: 0;
}

.trackerTop {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: 16px;
}

/* Small square tucked into the card's top-left corner. */
.backBtn {
  position: absolute;
  top: 0;
  left: 0;
  width: clamp(34px, 3.4vw, 44px);
  height: clamp(34px, 3.4vw, 44px);
  min-height: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: clamp(8px, 1vw, 12px);
  font-size: clamp(18px, 2vw, 26px);
  line-height: 1;
  background: rgba(255, 255, 255, 0.7);
}

.trackerName {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(28px, 3.4vw, 48px);
  text-align: center;
}

/* ---- glass reward tube ----
 * A horizontal glass capsule that fills from the left with the theme accent
 * as the class earns points. Starts empty; tapping adds a point. The gloss
 * layer sits above the fill so the whole tube reads as glass. */
.tube {
  position: relative;
  width: min(88%, 680px);
  height: clamp(58px, 8vw, 96px);
  border-radius: 999px;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  overflow: hidden;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0.08) 45%,
    rgba(255, 255, 255, 0.16) 100%
  );
  box-shadow:
    inset 0 2px 6px rgba(255, 255, 255, 0.55),
    inset 0 -8px 16px rgba(0, 0, 0, 0.18),
    inset 0 0 0 1px rgba(255, 255, 255, 0.22),
    0 12px 26px -10px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px) saturate(140%);
  -webkit-backdrop-filter: blur(6px) saturate(140%);
}

/* The accent-colored liquid. Width is set inline from progress. */
.tubeFill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--token-accent) 74%, white) 0%,
    var(--token-accent) 48%,
    color-mix(in srgb, var(--token-accent) 72%, black) 100%
  );
  box-shadow:
    inset 0 2px 3px rgba(255, 255, 255, 0.5),
    inset 0 -7px 12px rgba(0, 0, 0, 0.22);
  transition: width 340ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* Faint dividers marking each point, so the fill reads as discrete steps. */
.tubeTicks {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.tubeTick {
  position: absolute;
  top: 22%;
  bottom: 22%;
  width: 2px;
  transform: translateX(-1px);
  background: rgba(255, 255, 255, 0.4);
}

/* Specular reflection across the top of the glass, over the fill. */
.tubeGloss {
  position: absolute;
  left: 2.5%;
  right: 2.5%;
  top: 9%;
  height: 34%;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0));
  pointer-events: none;
}

/* ---- point buttons: a single +1 / +2 / +3 / +4 row ---- */
.points {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(10px, 1.6vw, 20px);
}

/* An accent-filled point button showing its value (+1 … +4). */
.pointBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(72px, 8vw, 108px);
  min-height: clamp(60px, 7vw, 92px);
  padding: 0 8px;
  border-radius: clamp(12px, 1.4vw, 18px);
  border: 1.5px solid rgba(255, 255, 255, 0.5);
  background: var(--token-accent);
  color: rgba(255, 255, 255, 0.98);
  font-family: var(--token-font-display);
  font-weight: 700;
  font-size: clamp(26px, 3vw, 44px);
  line-height: 1;
  cursor: pointer;
  box-shadow:
    inset 0 2px 0 rgba(255, 255, 255, 0.35),
    0 8px 18px -8px rgba(0, 0, 0, 0.45);
  transition:
    transform 90ms ease,
    filter 90ms ease;
}
.pointBtn:hover:not(:disabled) {
  transform: translateY(-2px);
  filter: brightness(1.06);
}
.pointBtn:active:not(:disabled) {
  transform: translateY(0);
}
.pointBtn:disabled {
  opacity: 0.4;
  cursor: default;
}

.earnedBanner {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(28px, 3.6vw, 52px);
  color: var(--token-accent);
  text-align: center;
}

.trackerHint {
  margin: 0;
  opacity: 0.8;
  font-size: clamp(16px, 1.6vw, 22px);
}

/* Deliberately below the glass-control base so these win: the tracker is
 * teacher-driven, and full-size pills pushed Undo/Reset off the card once
 * the earned banner appeared. */
.trackerControls {
  display: flex;
  gap: 12px;
}

.trackerControls .undoBtn,
.trackerControls .resetBtn,
.trackerControls .historyBtn {
  min-height: clamp(32px, 3.2vw, 40px);
  padding: 0 clamp(12px, 1.4vw, 18px);
  font-size: clamp(13px, 1.2vw, 16px);
}

/* ---- point history modal ---- */
.historyList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 52vh;
  overflow-y: auto;
}

.historyItem {
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.6vw, 20px);
  padding: clamp(8px, 1vw, 12px) clamp(10px, 1.2vw, 16px);
  border-radius: var(--token-radius-card);
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.historyDelta {
  flex: 0 0 auto;
  min-width: 2.4em;
  text-align: center;
  font-family: var(--token-font-display);
  font-weight: 700;
  font-size: clamp(20px, 2.2vw, 30px);
  color: var(--token-accent);
}
.historyDeltaNeg {
  color: rgba(150, 30, 20, 0.9);
}

.historyMeta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.historyTotal {
  font-weight: 600;
  font-size: clamp(14px, 1.4vw, 18px);
}

.historyDate {
  opacity: 0.7;
  font-size: clamp(12px, 1.2vw, 15px);
}

.resetBtn:disabled,
.undoBtn:disabled,
.addBtn:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ---- inline add / rename ---- */
.addInline {
  display: flex;
  gap: 8px;
  align-items: center;
}

.addInput,
.renameInput {
  min-height: var(--token-touch-min);
  padding: 0 14px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.9);
  color: var(--token-text-primary);
  font-family: inherit;
  font-size: clamp(15px, 1.4vw, 18px);
}

.renameInput {
  flex: 1;
  min-width: 0;
  font-family: var(--token-font-display);
  font-size: clamp(18px, 2vw, 26px);
}

.addBtn {
  background: var(--token-accent);
  color: rgba(255, 255, 255, 0.98);
  border-color: rgba(255, 255, 255, 0.35);
}
